import React, { useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, Plus, Trash2, Eye, AlertTriangle, Laptop, Smartphone, Monitor, RefreshCw, X, BombIcon, Search } from "lucide-react";

function DeviceIcon({ device }) {
  if (device === "iPhone" || device === "Android") return <Smartphone className="w-3.5 h-3.5" />;
  if (device === "iPad") return <Laptop className="w-3.5 h-3.5" />;
  return <Monitor className="w-3.5 h-3.5" />;
}

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleString();
}

function AccessLogTable({ logs, developerEmail }) {
  const eventColor = (event, isMaster, success) => {
    if (!success || event === "dev_access_denied" || event === "dev_page_view_unauthorized" || event === "login_failed")
      return { bg: "#FFD6CC", color: "#8b1a0e" };
    if (event === "dev_page_view" || event === "dev_api_call")
      return isMaster ? { bg: "#9AE6B4", color: "#0a0a0a" } : { bg: "#FFD166", color: "#5a3d00" };
    return { bg: "var(--app-surface-2)", color: "var(--app-text)" };
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead style={{ background: "var(--app-surface-2)" }}>
          <tr>
            <th className="p-3 text-left">When</th>
            <th className="p-3 text-left">Event</th>
            <th className="p-3 text-left">Email</th>
            <th className="p-3 text-left">Role</th>
            <th className="p-3 text-left">Device</th>
            <th className="p-3 text-left">Browser</th>
            <th className="p-3 text-left">IP</th>
            <th className="p-3 text-left">Path</th>
          </tr>
        </thead>
        <tbody>
          {logs.length === 0 && (
            <tr><td className="p-6 text-center" colSpan={8} style={{ color: "var(--app-text-muted)" }}>No access log entries yet.</td></tr>
          )}
          {logs.map((l) => {
            const flagged = (l.event === "dev_page_view" || l.event === "dev_api_call") && !l.is_master_email;
            const intrusion = l.event === "dev_access_denied" || l.event === "dev_page_view_unauthorized";
            const tone = eventColor(l.event, l.is_master_email, l.success);
            return (
              <tr key={l.id} className="border-t" style={{ borderColor: "var(--app-border)", background: intrusion ? "rgba(239,71,111,0.06)" : flagged ? "rgba(255,183,3,0.06)" : "transparent" }} data-testid={`log-${l.id}`}>
                <td className="p-3 whitespace-nowrap" title={l.created_at}>{timeAgo(l.created_at)}</td>
                <td className="p-3">
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: tone.bg, color: tone.color }}>
                    {intrusion && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                    {l.event}
                  </span>
                </td>
                <td className="p-3 font-mono text-xs" style={{ color: flagged || intrusion ? "#b71c1c" : "var(--app-text)" }}>
                  {l.email || "—"}
                  {l.is_master_email && <span className="ml-1 text-[10px] font-bold" style={{ color: "var(--app-primary)" }}>(MASTER)</span>}
                </td>
                <td className="p-3">{l.role || "—"}</td>
                <td className="p-3"><span className="inline-flex items-center gap-1"><DeviceIcon device={l.device} /> {l.device}</span></td>
                <td className="p-3">{l.browser}</td>
                <td className="p-3 font-mono text-xs">{l.ip}</td>
                <td className="p-3 font-mono text-xs">{l.path}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function DevDashboardPage() {
  const [metrics, setMetrics] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [phrases, setPhrases] = useState([]);
  const [newPhrase, setNewPhrase] = useState("");
  const [logs, setLogs] = useState([]);
  const [developerEmail, setDeveloperEmail] = useState("");
  const [logFilter, setLogFilter] = useState("all"); // all | dev | unauthorized

  // Drill-down dialog state — clicking a metric tile loads the records behind it
  const [drillKey, setDrillKey]   = useState(null);
  const [drillLabel, setDrillLabel] = useState("");
  const [drillData, setDrillData] = useState(null);  // {columns, rows, count} | null
  const [drillSearch, setDrillSearch] = useState("");
  const [wiping, setWiping] = useState(false);

  const openDrill = async (key, label) => {
    setDrillKey(key); setDrillLabel(label); setDrillData(null); setDrillSearch("");
    try {
      const r = await apiClient.get(`/dev/metric-detail/${key}`);
      setDrillData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load details");
      setDrillKey(null);
    }
  };
  const closeDrill = () => { setDrillKey(null); setDrillData(null); setDrillSearch(""); };

  const wipeAllUsers = async () => {
    const phrase = window.prompt(
      "⚠️ This will DELETE every user, student, payment, recital, song, invoice, and access log. " +
      "Your developer account stays. Type WIPE to confirm:"
    );
    if (phrase !== "WIPE") {
      toast.info("Wipe cancelled.");
      return;
    }
    setWiping(true);
    try {
      const r = await apiClient.post("/dev/wipe-all-users", {});
      const d = r.data?.deleted || {};
      toast.success(`Wiped: ${d.users || 0} users, ${d.student_profiles || 0} students, ${d.payment_transactions || 0} txns. Developer kept.`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
    } finally { setWiping(false); }
  };

  const loadLogs = async (filter = logFilter) => {
    const params = new URLSearchParams();
    if (filter === "dev") params.set("only_dev", "true");
    if (filter === "unauthorized") params.set("only_unauthorized", "true");
    const r = await apiClient.get(`/dev/access-logs?${params.toString()}`);
    setLogs(r.data.logs); setDeveloperEmail(r.data.developer_email);
  };

  const loadAll = async () => {
    const [m, t, p] = await Promise.all([
      apiClient.get("/dev/metrics"),
      apiClient.get("/dev/teachers"),
      apiClient.get("/greeting-phrases"),
    ]);
    setMetrics(m.data); setTeachers(t.data.teachers); setPhrases(p.data.phrases);
    await loadLogs();
  };

  useEffect(() => {
    // record that the developer panel was opened (or intrusion attempt)
    apiClient.post("/dev/page-view").catch(() => {});
    loadAll();
  }, []);

  useEffect(() => { loadLogs(logFilter); }, [logFilter]);

  const addPhrase = async () => {
    if (!newPhrase.trim()) return;
    await apiClient.post("/greeting-phrases", { phrase: newPhrase });
    setNewPhrase(""); const p = await apiClient.get("/greeting-phrases"); setPhrases(p.data.phrases);
    toast.success("Added");
  };
  const delPhrase = async (id) => {
    await apiClient.delete(`/greeting-phrases/${id}`);
    const p = await apiClient.get("/greeting-phrases"); setPhrases(p.data.phrases);
  };

  const clearLogs = async () => {
    if (!window.confirm("Clear ALL access log entries? This cannot be undone.")) return;
    await apiClient.delete("/dev/access-logs");
    await loadLogs();
    toast.success("Access log cleared");
  };

  const intrusions = logs.filter((l) => l.event === "dev_access_denied" || l.event === "dev_page_view_unauthorized" || (l.event === "login_failed" && (l.email || "").toLowerCase() === developerEmail));

  return (
    <div className="space-y-6" data-testid="dev-dashboard">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-8 h-8" style={{ color: "var(--app-accent)" }} />
          <div>
            <h1 className="font-display text-4xl font-bold">Developer Dashboard</h1>
            <p className="text-sm" style={{ color: "var(--app-text-muted)" }}>
              Restricted to <span className="font-mono font-bold">{developerEmail || "dashboardprodevelopment@gmail.com"}</span>. All access is logged.
            </p>
          </div>
        </div>
        <Button
          onClick={wipeAllUsers}
          disabled={wiping}
          className="btn-pill h-10 px-4"
          variant="outline"
          style={{ borderColor: "#EF476F", color: "#8b1a0e" }}
          data-testid="wipe-all-users-btn"
          title="Delete all users + data (keeps developer account)"
        >
          <BombIcon className="w-4 h-4" /> {wiping ? "Wiping..." : "Wipe all test users"}
        </Button>
      </div>

      {intrusions.length > 0 && (
        <div className="rounded-2xl p-4 flex items-start gap-3 border-2" style={{ background: "#FFF3F0", borderColor: "#EF476F" }} data-testid="intrusion-alert">
          <AlertTriangle className="w-5 h-5 mt-0.5" style={{ color: "#EF476F" }} />
          <div>
            <div className="font-bold" style={{ color: "#8b1a0e" }}>{intrusions.length} suspicious access attempt(s) detected</div>
            <div className="text-sm">Check the Access Log tab for details.</div>
          </div>
        </div>
      )}

      <Tabs defaultValue="metrics">
        <TabsList>
          <TabsTrigger value="metrics" data-testid="tab-metrics">Metrics</TabsTrigger>
          <TabsTrigger value="teachers" data-testid="tab-teachers">Teachers (paid vs actual)</TabsTrigger>
          <TabsTrigger value="phrases" data-testid="tab-phrases">Greeting phrases</TabsTrigger>
          <TabsTrigger value="access-log" data-testid="tab-access-log">
            <Eye className="w-4 h-4 mr-1" /> Access log
            {intrusions.length > 0 && <span className="ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-bold" style={{ background: "#EF476F", color: "#fff" }}>{intrusions.length}</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="metrics" className="mt-6">
          <p className="text-sm mb-3" style={{ color: "var(--app-text-muted)" }}>
            Tap any tile to see the actual records behind it (names, emails, timestamps, amounts).
          </p>
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
            {metrics && [
              ["total_teachers",          "Teachers",              metrics.total_teachers],
              ["total_student_accounts",  "Student accounts",      metrics.total_student_accounts],
              ["total_student_profiles",  "Student profiles",      metrics.total_student_profiles],
              ["paid_transactions",       "Paid transactions",     metrics.paid_transactions],
              ["active_paid_teachers",    "Active paid teachers",  metrics.active_paid_teachers],
              ["active_trial_teachers",   "On free trial",         metrics.active_trial_teachers],
              ["expired_teachers",        "Expired (must subscribe)", metrics.expired_teachers],
              ["possible_drift",          "Possible drift",        metrics.possible_drift],
              ["total_recitals",          "Total recitals",        metrics.total_recitals],
              ["total_practice_logs",     "Total practice logs",   metrics.total_practice_logs],
              ["total_songs",             "Total songs",           metrics.total_songs],
            ].map(([key, label, val]) => (
              <button
                key={key}
                type="button"
                onClick={() => openDrill(key, label)}
                className="bento-card p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2"
                data-testid={`devmetric-${key}`}
              >
                <div className="text-xs uppercase font-bold" style={{ color: "var(--app-text-muted)" }}>{label}</div>
                <div className="font-display text-3xl font-bold">{val ?? "—"}</div>
                <div className="text-xs mt-1 font-semibold" style={{ color: "var(--app-primary)" }}>View details →</div>
              </button>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="teachers" className="mt-6">
          <div className="bento-card p-5 overflow-x-auto">
            <h2 className="font-display font-bold text-xl mb-3">All teachers (paid vs actual)</h2>
            <table className="w-full text-sm">
              <thead style={{ background: "var(--app-surface-2)" }}>
                <tr><th className="p-3 text-left">Name</th><th className="p-3 text-left">Email</th><th className="p-3">Status</th><th className="p-3">Trial days left</th><th className="p-3">Expires</th></tr>
              </thead>
              <tbody>
                {teachers.map((t) => (
                  <tr key={t.id} className="border-t" style={{ borderColor: "var(--app-border)" }} data-testid={`devteacher-${t.id}`}>
                    <td className="p-3 font-semibold">{t.name}</td>
                    <td className="p-3">{t.email}</td>
                    <td className="p-3">
                      <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: t.subscription.active ? "#9AE6B4" : "#FFD6CC", color: "#0a0a0a" }}>
                        {t.subscription.active ? (t.subscription.trial ? "TRIAL" : "PAID") : "EXPIRED"}
                      </span>
                    </td>
                    <td className="p-3">{t.subscription.trial_days_left}</td>
                    <td className="p-3">{t.subscription.expires_at ? new Date(t.subscription.expires_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="phrases" className="mt-6">
          <div className="bento-card p-5">
            <h2 className="font-display font-bold text-xl mb-3">Rotating student greetings</h2>
            <div className="flex gap-2 mb-3">
              <Input value={newPhrase} onChange={(e) => setNewPhrase(e.target.value)} placeholder="New motivational phrase" data-testid="new-phrase-input" />
              <Button onClick={addPhrase} className="btn-pill btn-primary" data-testid="add-phrase-btn"><Plus className="w-4 h-4" /> Add</Button>
            </div>
            <ul className="space-y-2">
              {phrases.map((p) => (
                <li key={p.id} className="flex items-center justify-between p-3 rounded-xl border" style={{ borderColor: "var(--app-border)" }} data-testid={`phrase-${p.id}`}>
                  <span>{p.phrase}</span>
                  <Button variant="ghost" size="icon" onClick={() => delPhrase(p.id)}><Trash2 className="w-4 h-4" /></Button>
                </li>
              ))}
            </ul>
          </div>
        </TabsContent>

        <TabsContent value="access-log" className="mt-6">
          <div className="bento-card p-5 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="font-display font-bold text-xl">Access log</h2>
                <p className="text-sm" style={{ color: "var(--app-text-muted)" }}>
                  Every login, every developer panel visit, every device. Anything <b>not</b> using <span className="font-mono">{developerEmail}</span> is flagged in red/yellow.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant={logFilter === "all" ? "default" : "outline"} size="sm" onClick={() => setLogFilter("all")} data-testid="logfilter-all">All</Button>
                <Button variant={logFilter === "dev" ? "default" : "outline"} size="sm" onClick={() => setLogFilter("dev")} data-testid="logfilter-dev">Dev panel only</Button>
                <Button variant={logFilter === "unauthorized" ? "default" : "outline"} size="sm" onClick={() => setLogFilter("unauthorized")} data-testid="logfilter-unauthorized">Unauthorized</Button>
                <Button variant="outline" size="icon" onClick={() => loadLogs(logFilter)} data-testid="refresh-logs"><RefreshCw className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" onClick={clearLogs} data-testid="clear-logs">Clear</Button>
              </div>
            </div>
            <AccessLogTable logs={logs} developerEmail={developerEmail} />
          </div>
        </TabsContent>
      </Tabs>

      {/* Drill-down dialog: full table of records behind whichever metric tile was clicked */}
      <Dialog open={!!drillKey} onOpenChange={(o) => { if (!o) closeDrill(); }}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="drill-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" /> {drillLabel}
              {drillData?.count != null && (
                <span className="text-sm font-mono px-2 py-0.5 rounded-full" style={{ background: "var(--app-surface-2)" }}>
                  {drillData.count} records
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              Cross-check the total against the live records below.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2 mb-2">
            <Search className="w-4 h-4" style={{ color: "var(--app-text-muted)" }} />
            <Input
              value={drillSearch}
              onChange={(e) => setDrillSearch(e.target.value)}
              placeholder="Filter by name, email, or any cell..."
              data-testid="drill-search"
            />
          </div>

          <div className="overflow-auto flex-1 rounded-xl border" style={{ borderColor: "var(--app-border)" }}>
            {!drillData ? (
              <div className="p-10 text-center" style={{ color: "var(--app-text-muted)" }}>Loading…</div>
            ) : drillData.rows.length === 0 ? (
              <div className="p-10 text-center" style={{ color: "var(--app-text-muted)" }}>No records.</div>
            ) : (
              <table className="w-full text-sm">
                <thead style={{ background: "var(--app-surface-2)" }}>
                  <tr>
                    {drillData.columns.map((c) => (
                      <th key={c.key} className="p-3 text-left font-bold whitespace-nowrap">{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {drillData.rows
                    .filter((r) => {
                      if (!drillSearch.trim()) return true;
                      const q = drillSearch.toLowerCase();
                      return JSON.stringify(r).toLowerCase().includes(q);
                    })
                    .map((row, idx) => (
                      <tr key={row.id || row._id || `${drillKey}-${idx}`} className="border-t" style={{ borderColor: "var(--app-border)" }}>
                        {drillData.columns.map((c) => {
                          const raw = c.key.split(".").reduce((o, k) => (o ? o[k] : undefined), row);
                          let display = raw;
                          if (raw === undefined || raw === null || raw === "") display = "—";
                          else if (c.type === "datetime" || c.type === "date") {
                            try { display = new Date(raw).toLocaleString(); } catch { display = String(raw); }
                          } else if (c.type === "money")  display = `$${Number(raw).toFixed(2)}`;
                          else if (c.type === "bool")    display = raw ? "✓" : "—";
                          else if (typeof raw === "object") display = JSON.stringify(raw);
                          return <td key={c.key} className="p-3 align-top whitespace-nowrap">{String(display)}</td>;
                        })}
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
